/**
 * @file GetStarted.jsx
 * @description Handles user authentication flow (account check, login, account creation, OTP) for Marmasco.
 */

import React, { useState, useEffect, useRef } from "react";
import { runData } from "../functions/api/processor";
import { setSession } from "../utils/auth";
import { useProcessing } from "../context/ProcessingModal";
import { useReport } from "../context/ReportModal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Helmet } from "react-helmet";
import { setUserProfile } from "../account/profileData";
import CreateAccountModal from "./CreateAccountModal";
import OtpModal from "./OtpModal";
import {
  checkEmail,
  pinLogin,
  requestOtp,
  otpLogin,
  createAccount,
} from "./AuthService";
import { initiateGoogleLoginFlow } from "./GoogleAuth";
import { initiateFacebookLoginFlow } from "./FacebookAuth";
import {
  Col,
  Container,
  Row,
  Spinner,
  Button as BsButton,
  OverlayTrigger, // Import OverlayTrigger
  Tooltip, // Import Tooltip
} from "react-bootstrap";
import {
  FaChevronRight,
  FaSignInAlt,
  FaGoogle,
  FaFacebook,
} from "react-icons/fa";
import { localData } from "../functions/api/localData";
import PageHeader from "../components/PageHeader";

const GetStartedInternal = () => {
  // Processing & reporting contexts.
  const { setProcessing } = useProcessing();
  const { setReport } = useReport();

  // SEO state and fetching states.
  const [seoData, setSeoData] = useState({
    title: "Loading...",
    meta_description: "",
    keywords: "",
    og_title: "",
    og_description: "",
    og_image: "",
    canonical_url: window.location.href,
  });
  const [loadingSeo, setLoadingSeo] = useState(true);
  const [errorSeo, setErrorSeo] = useState(null);

  // Fetch SEO data on component mount
  useEffect(() => {
    (async () => {
      try {
        const response = await runData({ page: "start" }, "/web/pages/", "GET");
        if (response.status === 200 && response.data.seo) {
          let seoResponse = response.data.seo;
          // Ensure canonical URL is absolute
          if (
            seoResponse.canonical_url &&
            seoResponse.canonical_url.startsWith("/")
          ) {
            let hostname = window.location.hostname;
            if (!hostname.startsWith("www.")) {
              hostname = "www." + hostname;
            }
            seoResponse.canonical_url =
              window.location.protocol +
              "//" +
              hostname +
              seoResponse.canonical_url;
          }
          setSeoData(seoResponse);
        }
      } catch (err) {
        console.error("Failed to load SEO data:", err);
        setErrorSeo(err.message || "An error occurred while loading SEO data.");
      } finally {
        setLoadingSeo(false);
      }
    })();
  }, []);

  // Authentication and form states.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [hasAccount, setHasAccount] = useState(null); // null: unknown, true: exists, false: doesn't exist
  const [userName, setUserName] = useState("");
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);

  // OTP state
  const [otp, setOtp] = useState(Array(6).fill(""));
  const otpRefs = useRef([]);
  const otpKeys = [0, 1, 2, 3, 4, 5];
  const [otpRequested, setOtpRequested] = useState(false);
  const [lastOtpRequestTime, setLastOtpRequestTime] = useState(null);

  // Account creation modal state
  const [accountFormPage, setAccountFormPage] = useState(1);
  const [accountFormData, setAccountFormData] = useState({
    companyName: "",
    companyEmail: "",
    serviceOffered: [],
    useCompanyEmailForLogin: true,
    userEmail: "",
    phone: "",
    firstName: "",
    lastName: "",
  });

  const [isFbSdkReady, setIsFbSdkReady] = useState(false); // State for FB SDK readiness
  const [isGoogleSdkReady, setIsGoogleSdkReady] = useState(false); // State for Google SDK readiness

  // Generates dynamic meta description based on auth state
  const getCurrentDescription = () => {
    if (loadingSeo || !seoData.meta_description) {
      return ""; // Or a default loading message
    }
    if (hasAccount === null) {
      return "Enter your email to log in or create an account.";
    } else if (hasAccount) {
      return `Log in to manage your services.`;
    } else {
      // This state is usually brief as the modal shows up
      return `Complete the form to create your account.`;
    }
  };

  // Generates dynamic header text based on auth state
  const getHeaderText = () => {
    if (loadingSeo || !seoData.title) {
      return "Loading..."; // Or a default loading title
    }
    if (hasAccount === null) {
      // Use the fetched SEO title for the initial state
      return seoData.title || "Login or Create Account";
    } else if (hasAccount) {
      return `Welcome Back, ${userName}`;
    } else {
      // This state is usually brief as the modal shows up
      return "Create Your Account"; // Adjusted text for clarity
    }
  };

  // --- OTP Input Handlers ---
  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    if (!/^\d$/.test(value) && value !== "") return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        otpRefs.current[index - 1].focus();
      }
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1].focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("Text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;
    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
      if (otpRefs.current[i]) {
        otpRefs.current[i].value = pastedData[i];
      }
    }
    setOtp(newOtp);
    const nextIndex = pastedData.length >= 6 ? 5 : pastedData.length;
    if (otpRefs.current[nextIndex]) otpRefs.current[nextIndex].focus();
  };

  // Setup OTP paste listener
  useEffect(() => {
    if (otpRefs.current[0]) {
      otpRefs.current[0].addEventListener("paste", handleOtpPaste);
    }
    return () => {
      if (otpRefs.current[0]) {
        otpRefs.current[0].removeEventListener("paste", handleOtpPaste);
      }
    };
  }, []);

  // --- Authentication Flow Functions ---

  // Checks if email exists and updates UI state
  const handleEmailCheck = async (event) => {
    event.preventDefault();
    setProcessing(true);
    try {
      const response = await checkEmail(email);
      if (response.status === 200) {
        if (response.data.account === 1) {
          setHasAccount(true);
          setUserName(response.data.name);
        } else {
          setHasAccount(false);
          setShowCreateAccountModal(true);
        }
      } else {
        setReport({
          show: true,
          message:
            response.data.message || "An error occurred during email check.",
          type: "error",
        });
      }
    } catch (err) {
      setReport({
        show: true,
        message:
          err.message || "An unexpected error occurred during email check.",
        type: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  // Handles standard password login
  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);
    try {
      const response = await pinLogin(email, password);
      if (response.status === 200) {
        const { user } = response.data;
        const privacyStatus = await localData("get", "privacy");
        if (privacyStatus === "accept") {
          const privacyStatus = await localData("get", "privacy");
          if (privacyStatus === "accept") {
            await setUserProfile(user);
            setSession(true);
            setReport({
              show: true,
              message: "Logged in successfully using password!",
              type: "success",
            });
          } else {
            await localData("remove", "privacy");
            localStorage.removeItem("session");
            setReport({
              show: true,
              message:
                "Login successful. Please review and accept our Privacy Policy to proceed.",
              type: "info",
            });
          }
        } else {
          await localData("remove", "privacy");
          localStorage.removeItem("session");
          setReport({
            show: true,
            message:
              "Login successful. Please review and accept our Privacy Policy to proceed.",
            type: "info",
          });
        }
      } else {
        setReport({
          show: true,
          message: response.data.message || "An error occurred during login.",
          type: "error",
        });
      }
    } catch (err) {
      setReport({
        show: true,
        message: err.message || "Unexpected error during login.",
        type: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  // Requests OTP for login
  const handleRequestOtp = async () => {
    setShowOtpModal(false);
    const TWO_MINUTES = 2 * 60 * 1000;
    const now = Date.now();
    if (lastOtpRequestTime && now - lastOtpRequestTime < TWO_MINUTES) {
      const remainingTime = Math.ceil(
        (TWO_MINUTES - (now - lastOtpRequestTime)) / 60000
      );
      setReport({
        show: true,
        message: `Please wait ${remainingTime} minute(s) before requesting a new OTP.`,
        type: "error",
      });
      return;
    }
    setProcessing(true);
    try {
      const response = await requestOtp(email);
      if (response.status === 200) {
        setOtpRequested(true);
        setLastOtpRequestTime(now);
        setShowOtpModal(true);
      } else {
        setReport({
          show: true,
          message:
            response.data.message || "An error occurred while requesting OTP.",
          type: "error",
        });
      }
    } catch (err) {
      setReport({
        show: true,
        message:
          err.message || "An unexpected error occurred while requesting OTP.",
        type: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  // Handles OTP login submission
  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    setShowOtpModal(false);
    setProcessing(true);
    try {
      const otpStr = otp.join("").trim();
      const response = await otpLogin(email, otpStr);
      if (response.status === 200) {
        const { user } = response.data;
        const privacyStatus = await localData("get", "privacy");
        if (privacyStatus === "accept") {
          const privacyStatus = await localData("get", "privacy");
          if (privacyStatus === "accept") {
            user.last_refresh = new Date().toISOString();
            await setUserProfile(user);
            setSession(true);
            setReport({
              show: true,
              message: "Logged in successfully using OTP!",
              type: "success",
            });
          } else {
            await localData("remove", "privacy");
            localStorage.removeItem("session");
            setReport({
              show: true,
              message:
                "Login successful. Please review and accept our Privacy Policy to proceed.",
              type: "info",
            });
          }
        } else {
          await localData("remove", "privacy");
          localStorage.removeItem("session");
          setReport({
            show: true,
            message:
              "Login successful. Please review and accept our Privacy Policy to proceed.",
            type: "info",
          });
        }
      } else {
        setReport({
          show: true,
          message: response.data.message || "Invalid OTP. Please try again.",
          type: "error",
        });
      }
    } catch (err) {
      setReport({
        show: true,
        message: err.message || "Unexpected error during OTP login.",
        type: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  // Handles submission of the account creation form
  const handleAccountCreationSubmit = async (event) => {
    event.preventDefault();
    setShowCreateAccountModal(false);
    setProcessing(true);
    try {
      const response = await createAccount(accountFormData);
      if (response.status === 200) {
        setReport({
          show: true,
          message: response.data.message,
          type: "success",
        });
        setHasAccount(null);
      } else {
        setReport({
          show: true,
          message:
            response.data.message ||
            "An error occurred during account creation.",
          type: "error",
        });
      }
    } catch (err) {
      setReport({
        show: true,
        message:
          err.message ||
          "An unexpected error occurred during account creation.",
        type: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  // Effect to check for SDK readiness (Combined for Google and Facebook)
  useEffect(() => {
    let intervalId = null;
    let checksCompleted = { google: false, facebook: false };

    const checkSdks = () => {
      // Check Google SDK
      if (!checksCompleted.google) {
        if (typeof window.google !== 'undefined' && window.google.accounts && window.google.accounts.id) {
          console.log("Google SDK is ready.");
          setIsGoogleSdkReady(true);
          checksCompleted.google = true;
        } else {
          // console.log("Waiting for Google SDK..."); // Keep console log if desired
        }
      }

      // Check Facebook SDK
      if (!checksCompleted.facebook) {
         if (typeof window.FB !== 'undefined' && window.FB.XFBML) {
            console.log("Facebook SDK is ready.");
            setIsFbSdkReady(true);
            checksCompleted.facebook = true;
         } else {
            // console.log("Waiting for Facebook SDK..."); // Keep console log if desired
         }
      }

      // Clear interval if both SDKs are ready
      if (checksCompleted.google && checksCompleted.facebook && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
        console.log("All required SDKs ready.");
      }
    };

    // Start checking periodically
    intervalId = setInterval(checkSdks, 500);

    // Initial check
    checkSdks();

    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []); // Empty dependency array ensures this runs once on mount

  // --- Render Logic ---

  // Loading state for SEO data
  if (loadingSeo) {
    return (
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  // Error state for SEO data
  if (errorSeo) {
    return (
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "100vh" }}
      >
        <h2>Error: {errorSeo}</h2>
      </Container>
    );
  }

  // Tooltip for disabled Facebook button
  const fbDisabledTooltip = (props) => (
    <Tooltip id="fb-disabled-tooltip" {...props}>
      Loading Facebook Login...
    </Tooltip>
  );
  // Tooltip for disabled Google button
  const googleDisabledTooltip = (props) => (
    <Tooltip id="google-disabled-tooltip" {...props}>
      Loading Google Login...
    </Tooltip>
  );


  // Main page content
  return (
    <>
      <Helmet>
        <title>{getHeaderText()} - Marmasco Technologies</title>
        <meta name="description" content={getCurrentDescription()} />
        <meta name="keywords" content={seoData.keywords} />
        <meta
          property="og:title"
          content={`${getHeaderText()} - Marmasco Technologies`}
        />
        <meta property="og:description" content={getCurrentDescription()} />
        <meta property="og:image" content={seoData.og_image} />
        <link rel="canonical" href={seoData.canonical_url} />
        <script type="application/ld+json">
          {`
            {
              "@context": "http://schema.org",
              "@type": "WebPage",
              "name": "${getHeaderText()} - Marmasco Technologies",
              "url": "${seoData.canonical_url}",
              "description": "${getCurrentDescription()}"
            }
          `}
        </script>
      </Helmet>

      <PageHeader
        seoData={{
          ...seoData,
          title: getHeaderText(),
          meta_description: getCurrentDescription(),
        }}
      />

      <Container
        id="content-start"
        className="py-5 fade-in mb-4 rounded-4"
        style={{
          marginTop: "-10rem",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Row className="justify-content-center px-md-4 px-2">
              <Col xs={12} className="p-4 p-md-5 bg-light rounded-3 shadow-lg">
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (hasAccount === null) {
                      handleEmailCheck(e);
                    } else if (hasAccount) {
                      handleLoginSubmit(e);
                    } else {
                      setShowCreateAccountModal(true);
                    }
                  }}
                  className="mb-4"
                >
                  <Row className="gy-3 align-items-end">
                    {/* Initial state: Email input */}
                    {hasAccount === null && (
                      <>
                        <Col xs={12} md={8}>
                          <Form.Group>
                            <Form.Label className="fw-bold mb-1">
                              What's your email address?
                            </Form.Label>
                            <Form.Control
                              type="email"
                              placeholder="Enter email address"
                              value={email}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                setAccountFormData((prev) => ({
                                  ...prev,
                                  companyEmail: e.target.value,
                                  userEmail: e.target.value,
                                }));
                              }}
                              required
                              size="lg"
                              style={{ borderRadius: "0.375rem" }}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={4}>
                          <Button
                            type="submit"
                            variant="primary"
                            className="w-100"
                            size="lg"
                            style={{ borderRadius: "0.375rem" }}
                          >
                            Continue <FaChevronRight className="ms-2" />
                          </Button>
                        </Col>
                      </>
                    )}

                    {/* State: User has account, show password login */}
                    {hasAccount === true && (
                      <>
                        <Col xs={12} md={4}>
                          <Form.Group>
                            <Form.Label className="fw-bold mb-1">
                              Email
                            </Form.Label>
                            <Form.Control
                              type="email"
                              value={email}
                              required
                              disabled
                              size="lg"
                              style={{
                                borderRadius: "0.375rem",
                                backgroundColor: "#e9ecef",
                              }}
                            />
                            <div className="mt-1">
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => {
                                  setHasAccount(null);
                                  setEmail("");
                                  setPassword("");
                                }}
                                className="p-0 text-decoration-underline"
                              >
                                Not you, {userName}?
                              </Button>
                            </div>
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={4}>
                          <Form.Group>
                            <Form.Label className="fw-bold mb-1">
                              Password
                            </Form.Label>
                            <Form.Control
                              type="password"
                              placeholder="Enter password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              required
                              size="lg"
                              style={{ borderRadius: "0.375rem" }}
                            />
                            <div className="mt-1">
                              <Button
                                variant="link"
                                size="sm"
                                onClick={handleRequestOtp}
                                className="p-0 text-decoration-underline"
                              >
                                Login with One-Time Password
                              </Button>
                            </div>
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={4}>
                          <Button
                            type="submit"
                            variant="primary"
                            className="w-100"
                            size="lg"
                            style={{ borderRadius: "0.375rem" }}
                          >
                            Login <FaSignInAlt className="ms-2" />
                          </Button>
                        </Col>
                      </>
                    )}
                  </Row>
                </Form>

                {/* Social Login Section */}
                <div className="d-flex align-items-center my-4">
                  <hr className="flex-grow-1" />
                  <span className="mx-3 text-muted">CONTINUE WITH</span>
                  <hr className="flex-grow-1" />
                </div>
                <Row className="g-3">
                  <Col xs={12} md={6}>
                    {/* Conditionally render OverlayTrigger for Google Button */}
                    {!isGoogleSdkReady ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={googleDisabledTooltip}
                      >
                        <span className="d-inline-block w-100">
                          <BsButton
                            variant="outline-danger"
                            className="w-100 d-flex align-items-center justify-content-center"
                            size="lg"
                            disabled={true}
                            style={{
                              opacity: 0.65,
                              cursor: "not-allowed",
                              pointerEvents: 'none'
                            }}
                          >
                            <FaGoogle className="me-2" /> Google
                          </BsButton>
                        </span>
                      </OverlayTrigger>
                    ) : (
                      // Render enabled button directly
                      <BsButton
                        variant="outline-danger"
                        className="w-100 d-flex align-items-center justify-content-center"
                        onClick={() =>
                          initiateGoogleLoginFlow(setProcessing, setReport)
                        }
                        size="lg"
                        disabled={false}
                        style={{
                          opacity: 1,
                          cursor: "pointer",
                        }}
                      >
                        <FaGoogle className="me-2" /> Google
                      </BsButton>
                    )}
                  </Col>
                  <Col xs={12} md={6}>
                     {/* Conditionally render OverlayTrigger for Facebook Button */}
                     {!isFbSdkReady ? (
                       <OverlayTrigger
                          placement="top"
                          overlay={fbDisabledTooltip}
                       >
                         <span className="d-inline-block w-100">
                           <BsButton
                             variant="outline-primary"
                             className="w-100 d-flex align-items-center justify-content-center"
                             size="lg"
                             disabled={true}
                             style={{
                               "--bs-btn-color": "#1877F2",
                               "--bs-btn-border-color": "#1877F2",
                               opacity: 0.65,
                               cursor: "not-allowed",
                               pointerEvents: 'none'
                             }}
                           >
                             <FaFacebook className="me-2" /> Facebook
                           </BsButton>
                         </span>
                       </OverlayTrigger>
                     ) : (
                       // Render enabled button directly
                       <BsButton
                         variant="outline-primary"
                         className="w-100 d-flex align-items-center justify-content-center"
                         onClick={() =>
                           initiateFacebookLoginFlow(setProcessing, setReport)
                         }
                         size="lg"
                         disabled={false}
                         style={{
                           "--bs-btn-color": "#1877F2",
                           "--bs-btn-border-color": "#1877F2",
                           "--bs-btn-hover-bg": "#1877F2",
                           "--bs-btn-hover-color": "#fff",
                           "--bs-btn-active-bg": "#1877F2",
                           "--bs-btn-active-color": "#fff",
                           opacity: 1,
                           cursor: "pointer",
                         }}
                       >
                         <FaFacebook className="me-2" /> Facebook
                       </BsButton>
                     )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Modals */}
      <CreateAccountModal
        show={showCreateAccountModal}
        onHide={() => {
          setShowCreateAccountModal(false);
          if (hasAccount === false) {
            setHasAccount(null);
            setEmail("");
          }
        }}
        defaultEmail={email}
        accountFormData={accountFormData}
        setAccountFormData={setAccountFormData}
        accountFormPage={accountFormPage}
        setAccountFormPage={setAccountFormPage}
        handleAccountCreationSubmit={handleAccountCreationSubmit}
      />
      <OtpModal
        show={showOtpModal}
        onHide={() => setShowOtpModal(false)}
        otp={otp}
        otpRefs={otpRefs}
        otpKeys={otpKeys}
        handleOtpChange={handleOtpChange}
        handleOtpKeyDown={handleOtpKeyDown}
        handleOtpSubmit={handleOtpSubmit}
        handleRequestOtp={handleRequestOtp}
        otpRequested={otpRequested}
      />
    </>
  );
};

// Component Wrapper (checks for environment variables)
const GetStarted = () => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!googleClientId) {
    console.warn(
      "Google Client ID not found. Google Login might fail. Set REACT_APP_GOOGLE_CLIENT_ID."
    );
  }
  const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
  if (!facebookAppId) {
    console.warn(
      "Facebook App ID not found. Facebook Login might fail. Set REACT_APP_FACEBOOK_APP_ID."
    );
  }

  return <GetStartedInternal />;
};

export default GetStarted;
